import type { Messages } from './fr';

const es: Messages = {
  app: { tagline: 'El juego de letras que habla varios idiomas.' },
  nav: {
    back: 'Volver',
    home: 'Inicio',
    rules: 'Reglas',
    settings: 'Ajustes',
    about: 'Acerca de',
    account: 'Cuenta',
    leaderboards: 'Clasificaciones',
    tournaments: 'Torneos',
    daily: 'Reto del día',
    admin: 'Administración',
  },
  common: {
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    close: 'Cerrar',
    save: 'Guardar',
    loading: 'Cargando…',
    yes: 'Sí',
    no: 'No',
    copy: 'Copiar',
    copied: '¡Copiado!',
    retry: 'Reintentar',
  },
  langNames: {
    fr: 'francés',
    en: 'inglés',
    es: 'español',
    de: 'alemán',
    it: 'italiano',
    pt: 'portugués',
  },
  home: {
    quickPlay: 'Jugar',
    quickPlayDesc: 'Partida Clásica de 2 minutos · {{lang}}',
    modesTitle: 'Modos de juego',
    newGame: 'Nueva partida',
    online: 'Jugar en línea',
  },
  modes: {
    classic: {
      name: 'Clásico',
      short: 'Un idioma · 2 min',
      desc: 'Un solo idioma, el mismo para todos. Consigue el máximo de puntos en 120 segundos. Sin bonificación de idioma ni por terminar antes.',
    },
    polyglot: {
      name: 'Políglota común',
      short: '2 o 3 idiomas para todos · 2 min',
      desc: 'Los mismos 2 o 3 idiomas para todos. Bonificación de diversidad (+20 % con dos idiomas, +30 % con tres) si encuentras al menos 3 palabras en cada idioma.',
    },
    duel: {
      name: 'Duelo táctico',
      short: '10 palabras o 2 min · contrato',
      desc: 'Cada jugador elige en secreto 1, 2 o 3 idiomas. Objetivo: 10 palabras válidas. Contrato lingüístico + bonificación de velocidad si llegas a 10 palabras antes del final. La 10.ª palabra termina tu ronda.',
    },
    sprint: {
      name: 'Sprint libre',
      short: '1 a 3 idiomas · 2 min · contrato',
      desc: 'Elige de 1 a 3 idiomas y consigue el máximo de puntos en 120 segundos con contrato lingüístico. Sin bonificación por terminar antes.',
    },
    relax: {
      name: 'Relax',
      short: 'Sin cronómetro',
      desc: 'Sin límite de tiempo y sin clasificación. Las pistas de validez solo pueden activarse en este modo.',
    },
  },
  setup: {
    title: 'Nueva partida',
    mode: 'Modo',
    languagesShared: 'Idiomas de la partida ({{min}} a {{max}})',
    languagesIndividual: 'Tus idiomas (1 a 3, se bloquean antes del sorteo)',
    errors: {
      duplicate_language: 'Cada idioma solo puede elegirse una vez.',
      too_few_languages: 'Elige al menos {{min}} idioma(s).',
      too_many_languages: 'Como máximo {{max}} idioma(s).',
    },
    noLanguageBonus: 'Sin bonificación de idioma en este modo.',
    opponent: 'Rival',
    noOpponent: 'Ninguno (solo)',
    robotNotice:
      'Los robots son programas de entrenamiento claramente identificados. Nunca cuentan en las clasificaciones.',
    robotSameLanguages: 'El robot juega con mis idiomas',
    robotLanguages: 'Idiomas del robot',
    loading: 'Preparando el sorteo…',
    start: 'Empezar',
    noDraw:
      'No se encontró un sorteo equilibrado para esta combinación. Prueba otros idiomas o el contrato Especialista.',
  },
  contracts: {
    specialist: {
      name: 'Especialista',
      desc: 'un idioma, bonificación asegurada sobre tus puntos base.',
    },
    duo: {
      name: 'Dúo',
      desc: 'dos idiomas, bonificación solo con al menos 3 palabras distintas en cada uno.',
    },
    trio: {
      name: 'Trío',
      desc: 'tres idiomas, bonificación solo con al menos 3 palabras distintas en cada uno.',
    },
    diversity: {
      name: 'Diversidad',
      desc: 'bonificación del {{pct}} % para todos con al menos 3 palabras por idioma.',
    },
    short: { specialist: 'Especialista', duo: 'Dúo', trio: 'Trío', diversity: 'Diversidad' },
  },
  bots: {
    rookie: 'Principiante',
    casual: 'Intermedio',
    skilled: 'Avanzado',
    expert: 'Experto',
    rookieDesc: 'Palabras muy comunes, ritmo lento, prefiere palabras cortas.',
    casualDesc: 'Palabras comunes, ritmo moderado.',
    skilledDesc: 'Vocabulario común amplio, ritmo sostenido.',
    expertDesc: 'También conoce palabras raras, rápido, busca palabras largas.',
    robotName: 'Robot {{level}}',
  },
  game: {
    timeLeft: 'Tiempo restante',
    getReady: 'Preparados…',
    untimed: '∞',
    score: 'Puntuación',
    points: 'pts',
    wordsOfTarget: '{{n}} / {{target}} palabras',
    wordsCount_one: '{{count}} palabra',
    wordsCount_other: '{{count}} palabras',
    languageCounters: 'Palabras por idioma',
    robotNotice: 'Rival robot (programa de entrenamiento)',
    opponentDone: 'terminado',
    finalWarning:
      'Atención: tu próxima palabra válida será la 10.ª y terminará la ronda. Tu contrato aún no está cumplido.',
    currentWord: 'Palabra actual',
    tapLetters: 'Toca las letras',
    hintValid: 'Válida: {{langs}}',
    hintInvalid: 'Todavía no es una palabra',
    attributeTo: 'Asignar a {{lang}}',
    attribution: 'Idioma:',
    auto: 'Auto',
    autoExplain:
      'Auto: la palabra se asigna al idioma válido que más palabras necesita para tu contrato; si no, según el orden de tus idiomas.',
    rack: 'Letras del sorteo',
    tileLabel: 'Letra {{letter}}, posición {{pos}}',
    shuffle: 'Mezclar',
    delete: 'Borrar',
    clear: 'Vaciar',
    submit: 'Validar',
    foundWords: 'Palabras encontradas',
    bonusSecured: 'bonificación de idioma asegurada',
    finish: 'Terminar',
    stop: 'Parar',
    stopTitle: '¿Parar la ronda?',
    stopBody: 'Tus palabras se conservan. Parar antes no da ninguna bonificación.',
    finishBody: '¿Terminar la partida relax y ver el resumen?',
    confirmFinalTitle: 'Última palabra: contrato no cumplido',
    confirmFinalOk: 'Validar de todos modos',
    confirmFinalBody:
      'Esta 10.ª palabra termina tu ronda de inmediato. Tu contrato lingüístico no se cumplirá: sin bonificación de idioma (la de velocidad se mantiene).',
    accepted: '«{{word}}» +{{points}} ({{lang}})',
  },
  reasons: {
    round_over: 'La ronda ha terminado.',
    invalid_characters: 'Caracteres no jugables.',
    too_short: 'Mínimo tres letras.',
    too_long: 'Palabra demasiado larga.',
    not_formable: 'No es posible con estas letras.',
    duplicate: 'Ya encontrada.',
    language_not_allowed: 'Idioma no permitido en esta ronda.',
    excluded_word: 'Palabra excluida por la política de contenido del juego.',
    not_a_word: 'Palabra desconocida en tus idiomas.',
    wrong_language: 'Esta palabra es válida en: {{langs}}.',
    confirmation_required: 'Se necesita confirmación.',
    rate_limited: 'Demasiados intentos, ve más despacio.',
    not_started: 'La ronda aún no ha empezado.',
  },
  results: {
    points: 'puntos',
    pts: 'pts',
    base: 'Puntos base',
    fulfilled: 'cumplido',
    missed: 'no cumplido',
    speedBonus: 'Bonificación de velocidad',
    officialTime: 'Tiempo oficial',
    target: 'Objetivo',
    endReason: {
      target_reached: 'Objetivo alcanzado',
      time_up: 'Tiempo agotado',
      stopped: 'Parada voluntaria',
      abandoned: 'Abandono',
    },
    explainBase: 'Tus palabras suman {{base}} puntos base (10 × (letras − 2)).',
    explainSpecialist: 'Contrato Especialista: +10 % asegurado.',
    explainQuotaOk:
      'Al menos {{quota}} palabras en cada idioma: bonificación del {{pct}} % obtenida.',
    explainQuotaKo:
      'Hacían falta al menos {{quota}} palabras en cada idioma: sin bonificación de idioma ({{pct}} % posible).',
    explainSpeedOk: '10 palabras alcanzadas en {{time}}: bonificación de velocidad del {{pct}} %.',
    explainSpeedKo: 'Objetivo de 10 palabras no alcanzado a tiempo: sin bonificación de velocidad.',
    explainFormula:
      'Total = base × (1 + bonificación de idioma + bonificación de velocidad), redondeado una sola vez a la centésima.',
    win: '¡Victoria!',
    lose: 'Derrota',
    tie: 'Empate',
    tieExplain: 'Puntuaciones exactamente idénticas: el encuentro se declara empate.',
    you: 'Tú',
    robotTitle: '{{name}} (robot)',
    missedWords: 'Palabras comunes que podías formar',
    missedWordsNote:
      'Lista orientativa de palabras frecuentes de tus idiomas, para la próxima vez.',
    shareText: '{{mode}} ({{langs}}): {{score}} puntos, {{words}} palabras. ¿Quién lo supera?',
    playAgain: 'Jugar otra vez',
    share: 'Compartir resultado',
    changeSetup: 'Cambiar de modo',
    localNotice: 'Partida local: no entra en ninguna clasificación.',
    savedToHistory: 'Guardada en «Mis partidas» (entrenamiento).',
    drawAudit: 'Sorteo {{seed}} ({{gen}})',
  },
  errors: {
    network: 'Conexión perdida. Reintentando…',
    lexiconLoad: 'No se pudo cargar el diccionario. Comprueba la conexión e inténtalo de nuevo.',
    notFound: 'Página no encontrada',
    generic: 'Se ha producido un error.',
  },
  online: {
    title: 'En línea',
    challengeFriend: 'Retar a un amigo',
    challengeFriendDesc: 'Enlace de invitación, cada uno juega cuando quiere',
    liveDuel: 'Duelo en directo',
    liveDuelDesc: 'Sala, invitación, partida simultánea',
    myGames: 'Mis partidas',
    offlineNote: 'Servidor inaccesible: solo están disponibles el modo solo y el entrenamiento.',
    guestBanner:
      'Juegas como invitado. Crea una cuenta para guardar tu historial y entrar en las clasificaciones.',
    saveAccount: 'Guardar mi cuenta',
  },
  challenge: {
    newTitle: 'Retar a un amigo',
    returnMatch: 'Ida y vuelta (2 rondas, sorteos cruzados, sin clasificación)',
    create: 'Crear el reto',
    created: '¡Reto creado! Envía este enlace:',
    share: 'Compartir el enlace',
    shareText: '¡Te reto ({{mode}})!',
    expires: 'Caduca el {{date}}',
    playMine: 'Jugar mi ronda ahora',
    inviteFrom: '¡{{name}} te reta!',
    acceptTitle: 'Invitación',
    chooseLanguages: 'Elige tus idiomas (se bloquean al confirmar)',
    compatibleOnly: 'Solo se proponen las combinaciones compatibles con el sorteo de este reto.',
    accept: 'Aceptar el reto',
    expired: 'Este reto ha caducado.',
    taken: 'Otra persona ya ha aceptado este reto.',
    own: 'Es tu propio reto: comparte el enlace con un amigo.',
    notFound: 'Reto no encontrado.',
    returnInfo:
      'Ida y vuelta: dos rondas con dos sorteos distintos; no se revela ningún resultado antes del final.',
    goToMatch: 'Ir a la partida',
  },
  match: {
    readyTitle: 'Tu ronda',
    readyBody:
      'El cronómetro empieza en cuanto aparecen las letras. Cerrar o recargar la página no reinicia el tiempo.',
    start: 'Empezar mi ronda',
    waitingOpponent:
      'Esperando a tu rival. Sus palabras y su puntuación permanecen ocultas hasta el final.',
    opponentProgress: '{{name}}: {{count}} palabra(s)',
    leg: 'Ronda {{n}} / 2',
    nextLeg: 'Jugar la ronda siguiente',
    encounterTotal: 'Total del encuentro',
    unranked: 'Partida sin clasificación',
    ranked: 'Partida clasificada',
    reportWord: 'Señalar una palabra rechazada',
    reportPrompt: 'Palabra a señalar (e idioma)',
    reportSent: 'Gracias, señalamiento enviado.',
    loadError: 'No se pudo cargar la partida.',
    opponentNotPlayed: 'El rival no jugó.',
    opponent: 'Rival',
  },
  account: {
    title: 'Mi cuenta',
    guest: 'Cuenta de invitado',
    registered: 'Cuenta guardada',
    register: 'Crear una cuenta',
    login: 'Iniciar sesión',
    logout: 'Cerrar sesión',
    username: 'Usuario (3–20 caracteres)',
    password: 'Contraseña (mín. 10 caracteres)',
    email: 'Correo electrónico (opcional)',
    displayName: 'Nombre visible',
    saveHint: 'Tu historial de invitado se conserva al crear la cuenta.',
    history: 'Historial',
    export: 'Exportar mis datos',
    delete: 'Eliminar mi cuenta',
    deleteConfirm: '¿Eliminar definitivamente tu cuenta? Tus datos personales se borrarán.',
    notifications: 'Recordatorios',
    remindersOptIn: 'Recibir recordatorios (como máximo uno al día, nunca en horas de descanso)',
    quietHours: 'Horas de descanso (de … a …)',
    haveAccount: '¿Ya tienes cuenta?',
    noAccount: '¿Aún no tienes cuenta?',
    saved: 'Guardado.',
    errors: {
      invalid_credentials: 'Usuario o contraseña incorrectos.',
      username_or_email_taken: 'Usuario o correo ya en uso.',
      nickname_refused: 'Este nombre no está permitido.',
      nickname_reserved: 'Este nombre está reservado.',
      nickname_length: 'Entre 3 y 20 caracteres.',
      nickname_characters: 'Caracteres no permitidos.',
      invalid_request: 'Revisa los campos.',
      account_suspended: 'Cuenta suspendida.',
      generic: 'Se ha producido un error.',
    },
  },
  live: {
    searching: 'Buscando rival…',
    searchingBody: 'Espera: {{seconds}} s',
    noFakePlayers: 'Aquí solo se proponen jugadores reales; ningún robot se presenta como humano.',
    fallbackTitle: 'Nadie por ahora. Puedes:',
    fallbackRobot: 'Jugar contra un robot (entrenamiento, sin clasificación)',
    fallbackAsync: 'Crear un reto asíncrono para compartir',
    othersSearching_zero: 'Ningún otro jugador busca este tipo de partida ahora mismo.',
    othersSearching_one: '{{count}} jugador más busca este tipo de partida ahora mismo.',
    othersSearching_other: '{{count}} jugadores más buscan este tipo de partida ahora mismo.',
    noOpponentFound: 'No hay nadie disponible por ahora. Puedes:',
    searchAgain: 'Buscar de nuevo',
    languagesSecret: 'Tus idiomas (secretos, bloqueados antes del sorteo)',
    quick: 'Partida rápida',
    createRoom: 'Crear una sala e invitar',
    rankedNote:
      'Las partidas entre cuentas guardadas cuentan para la clasificación (oficial o experimental según el modo).',
    roomFull: 'Esta sala está completa.',
    roomNotFound: 'Sala no encontrada o caducada.',
    offline: 'desconectado',
    online: 'conectado',
    opponentReady: 'listo',
    waitingOpponent: 'Esperando a un rival…',
    shareRoom: 'Invita a un amigo con este enlace:',
    sharedReady: 'Idiomas fijados para ambos jugadores.',
    lock: 'Estoy listo',
    waitingLock: 'Esperando la elección del rival…',
    noDrawForBoth: 'No hay sorteo equilibrado para ambas configuraciones. Elige otros idiomas.',
    disconnectRule:
      'Regla de desconexión: el reloj del servidor sigue; vuelve a esta página para retomar tu ronda donde está.',
    opponentLeft:
      '{{name}} se ha desconectado. Su cronómetro sigue; puede volver antes del final de su ronda.',
    opponentBack: '{{name}} ha vuelto.',
    reconnecting: 'Conexión interrumpida, reconectando… Las palabras ya validadas se conservan.',
    rematch: 'Revancha',
    joinRematch: 'Unirse a la revancha',
    reactionsTitle: 'Reacciones',
    reactions: {
      gg: '¡Bien jugado!',
      bravo: '¡Bravo!',
      wow: '¡Impresionante!',
      oops: '¡Uy!',
      thanks: '¡Gracias!',
      good_luck: '¡Suerte!',
    },
  },
  boards: {
    title: 'Clasificaciones',
    official: 'Oficial',
    experimental: 'Experimental',
    officialDesc: 'Modos simétricos (Clásico, Políglota común): mismos idiomas para todos.',
    experimentalDesc:
      'Duelo táctico y Sprint libre: clasificación aparte, no comparable con los modos simétricos.',
    myRank: 'Tu puesto: {{rank}} · {{rating}} Elo · {{games}} partidas',
    empty: 'Aún no hay resultados.',
    player: 'Jugador',
    rating: 'Elo',
    games: 'Partidas',
    rules:
      'Solo cuentan las partidas entre cuentas guardadas. Robots, partidas locales y encuentros de ida y vuelta nunca entran en la clasificación.',
  },
  tournaments: {
    title: 'Torneos',
    intro:
      'Torneos semanales asíncronos: varias rondas, un intento por ronda, las mismas letras para todos.',
    none: 'No hay torneos por ahora.',
    weeklyTitle: 'Semanal {{mode}} {{langs}} · {{period}}',
    rounds: '{{n}} ronda(s)',
    entrants: '{{n}} inscrito(s)',
    registered: 'inscrito',
    status: {
      scheduled: 'Próximo',
      open: 'En curso',
      closing: 'Cierre',
      closed: 'Terminado',
      cancelled: 'Cancelado',
    },
    frozen: 'Reglas {{rules}} y diccionarios {{lexicons}} fijados para todo el torneo.',
    attempts: '{{n}} intento(s) por ronda.',
    sharingLimit:
      'Límite conocido: en una competición asíncrona pública, los jugadores pueden compartir respuestas. El juego no pretende impedirlo por completo.',
    register: 'Inscribirme',
    accountRequired: 'Se necesita una cuenta guardada para jugar torneos.',
    myRounds: 'Mis rondas',
    round: 'Ronda {{n}}',
    resume: 'Retomar',
    play: 'Jugar',
    standings: 'Clasificación del torneo',
    roundsPlayed: '{{n}} ronda(s)',
    tieRule: 'Empate exacto: mismo puesto, sin desempate oculto.',
    backToTournament: 'Volver al torneo',
    finalStandings: 'Clasificación final',
    myResult: 'Tu total: {{total}} puntos · puesto {{rank}} de {{n}}',
    noRoundYet: 'Aún no has jugado ninguna ronda.',
    startsAt: 'Inicio: {{date}}',
    abandoned: 'Sin terminar',
    retry: 'Nuevo intento ({{n}} restante(s))',
    closingNote:
      'Cierre en curso: las rondas ya empezadas pueden terminar; la clasificación final llega en unos minutos.',
    errors: {
      competition_not_found: 'Torneo no encontrado.',
      invitation_required: 'Este torneo es privado: se necesita una invitación.',
      attempts_exhausted: 'Intento ya usado en esta ronda.',
      competition_closed: 'El torneo ha terminado.',
      competition_not_started: 'El torneo no ha empezado.',
      not_registered: 'Inscríbete primero.',
      account_required: 'Se necesita una cuenta guardada.',
    },
  },
  daily: {
    title: 'Reto del día',
    intro: 'Un sorteo por categoría y por día (UTC), el mismo para todos. Un solo intento.',
    back: 'Volver a los retos del día',
    play: 'Jugar',
    inProgress: 'En curso',
    resets: 'Nuevo reto el {{time}}.',
  },
  profile: {
    title: 'Mi perfil',
    streak: 'días de racha',
    bestStreak: 'mejor racha',
    rounds: 'partidas en línea',
    distinctWords: 'palabras distintas',
    streakRules:
      'La racha gana un día cuando terminas una partida en línea con al menos una palabra válida (reto del día, reto entre amigos, duelo en directo o torneo), como máximo una vez al día. Días de gracia gratuitos restantes: {{grace}} (se renuevan cada 30 días). Zona horaria: {{tz}}.',
    useMyTimezone: 'Usar mi zona horaria ({{tz}})',
    tzTooSoon: 'La zona horaria solo puede cambiarse una vez cada 7 días.',
    badges: 'Insignias',
    noBadges: 'Aún no hay insignias.',
    badgesNote: 'Las insignias solo se basan en eventos verificados por el servidor.',
    cosmetics: 'Personalización',
    cosmeticsNote: 'Puramente visual: sin efecto en la puntuación ni en los sorteos.',
    plusOnly: 'Solo Plus',
    earnIt: 'Se desbloquea jugando',
    slot: {
      theme: 'Tema',
      avatar: 'Avatar',
      frame: 'Marco',
    },
    history: 'Mis partidas',
    gamesSummary: '{{online}} en línea · {{practice}} entrenamiento(s)',
    noGames: 'Todavía no hay partidas guardadas.',
    pendingSync: '{{n}} entrenamiento(s) pendiente(s) de envío (sin conexión).',
    gamesNote:
      'Los entrenamientos (solo, robots, relax) se juegan en tu dispositivo y se guardan aquí. El servidor comprueba las palabras y recalcula la puntuación, pero no puede verificar el tiempo: no cuentan para la racha, las insignias ni las clasificaciones.',
    sourceOnline: 'En línea',
    sourcePractice: 'Entrenamiento',
    waitingOpponent: 'resultado cuando juegue el rival',
    playDaily: 'Jugar el reto del día',
  },
  badges: {
    first_round: 'Primera ronda',
    nine_letters: 'Palabra de 9 letras',
    trio_contract: 'Contrato Trío cumplido',
    speed_60: '10 palabras en menos de 60 s',
    words_100: '100 palabras encontradas',
    words_1000: '1000 palabras encontradas',
    streak_7: 'Racha de 7 días',
    streak_30: 'Racha de 30 días',
    first_duel_win: 'Primera victoria en duelo',
    tournament_participant: 'Participante de torneo',
    tournament_winner: 'Ganador de torneo',
    tournament_podium: 'Podio de torneo',
    tournament_top10: 'Top 10 de torneo',
    daily_participant: 'Reto del día',
    team_weekly: 'Objetivo de equipo logrado',
  },
  cosmetics: {
    theme_default: 'Clásico',
    theme_ocean: 'Océano',
    theme_aurora: 'Aurora',
    theme_sakura: 'Sakura',
    theme_mono: 'Monocromo',
    avatar_letter: 'Inicial',
    avatar_owl: 'Búho',
    avatar_fox: 'Zorro',
    frame_none: 'Ninguno',
    frame_team: 'Equipo',
    frame_champion: 'Campeón',
  },
  reminders: {
    title: 'Recordatorios',
    explain:
      'Opcionales: como máximo uno al día, nunca en tus horas de descanso y ninguno si ya has jugado. Desactivables en cualquier momento.',
    unavailable: 'Los recordatorios no están disponibles en este servidor o dispositivo.',
    quietStart: 'Horas de descanso: inicio',
    quietEnd: 'Horas de descanso: fin',
    enable: 'Activar recordatorios',
    disable: 'Desactivar',
    enabled: 'Recordatorios activados.',
    disabled: 'Recordatorios desactivados.',
    denied: 'Permiso denegado por el navegador.',
  },
  journal: {
    title: 'Cuaderno de palabras',
    intro: 'Las palabras que has encontrado en línea, por idioma.',
    all: 'Todas',
    search: 'Buscar una palabra',
    empty: 'Aún no hay palabras.',
  },
  friends: {
    title: 'Amigos',
    inbox: 'Retos recibidos',
    inboxNote: 'Abre el enlace que te envió tu amigo para jugar.',
    invite: 'Invitar a un amigo',
    inviteNote: 'Enlace válido 7 días. Sin chat libre: solo retos y reacciones predefinidas.',
    none: 'Aún no tienes amigos.',
    challenge: 'Retar',
    remove: 'Quitar',
    ownInvite: 'Es tu propia invitación.',
    inviteInvalid: 'Invitación no válida o caducada.',
  },
  team: {
    title: 'Equipo',
    intro:
      'Únete o crea un equipo (máx. 20 miembros) y alcanzad juntos un objetivo semanal de palabras. Recompensas solo cosméticas.',
    name: 'Nombre del equipo',
    create: 'Crear equipo',
    weekly: 'Objetivo de la semana {{week}}',
    progress: '{{words}} / {{goal}} palabras',
    rewardNote: 'Objetivo logrado: marco «Equipo» para cada miembro.',
    members: 'Miembros ({{n}})',
    leave: 'Salir del equipo',
    accountRequired: 'Se necesita una cuenta guardada.',
    joinFailed: 'No se pudo unir a este equipo.',
  },
  moderation: {
    block: 'Bloquear a este jugador',
    blocked: 'Jugador bloqueado.',
    report: 'Señalar…',
    reported: 'Señalamiento enviado.',
    reasons: {
      nickname: 'Nombre inapropiado',
      cheating: 'Posible trampa',
      harassment: 'Acoso',
      other: 'Otro',
    },
  },
  plus: {
    intro: 'Plus apoya el juego y añade herramientas, sin tocar nunca la puntuación:',
    fairness:
      'El núcleo del juego, los seis idiomas, el entrenamiento, los retos y la competición pública siguen siendo gratuitos. Ningún ajuste de dificultad depende de la suscripción.',
    features: {
      advanced_stats: 'Estadísticas avanzadas',
      challenge_archives: 'Archivo completo de retos terminados',
      personal_training: 'Entrenamiento personalizado determinista',
      customisation: 'Temas y avatares exclusivos',
      private_tournaments: 'Herramientas de torneos privados',
    },
    monthly: 'Suscribirme — {{price}}',
    yearly: 'Plan anual — {{price}}',
    unavailable: 'Plus aún no está disponible para la compra.',
    testMode: 'Modo de prueba: ningún pago real.',
    confirming: 'Confirmando el pago… (solo se activa cuando el proveedor lo confirma)',
    cancelled: 'Pago cancelado.',
    active: 'Plus activo',
    until: 'hasta el {{date}}',
    manage: 'Gestionar mi suscripción',
    stats: 'Estadísticas',
    mode: 'Modo',
    rounds: 'Rondas',
    avgScore: 'Puntuación media',
    contractRate: 'Contratos cumplidos',
    secPerWord: 's / palabra',
    train: 'Entrenamiento personalizado ({{lang}})',
    archive: 'Archivo de retos terminados',
    archiveEmpty: 'Todavía no hay partidas terminadas.',
    archiveMore: 'Mostrar más',
    archiveBot: 'robot',
    pricesNote:
      'Precios orientativos que pueden cambiar; el importe exacto se muestra en la página de pago.',
    notOffered: 'Esta versión no ofrece ninguna suscripción: todo el juego es gratuito.',
  },
  legal: {
    draftBanner: 'Borrador: pendiente de completar con la identidad real del editor.',
    privacy: 'Privacidad',
    terms: 'Condiciones',
    notice: 'Aviso legal',
  },
  offline: {
    title: 'Jugar sin conexión',
    explain:
      'Descarga los diccionarios para jugar solo y entrenar sin conexión. Las partidas sin conexión no entran en ninguna clasificación.',
    download: 'Descargar',
    ready: 'Disponible',
  },
  play: { noGame: 'No hay ninguna partida en curso.' },
  install: {
    body: 'Instala el juego en tu pantalla de inicio para jugar más rápido, incluso sin conexión en solitario.',
    cta: 'Instalar',
    later: 'Más tarde',
  },
  pwa: {
    updateReady: 'Hay una nueva versión lista.',
    reload: 'Actualizar',
    offline:
      'Sin conexión: el modo solo y el entrenamiento siguen disponibles con los diccionarios ya descargados.',
  },
  settings: {
    title: 'Ajustes',
    uiLanguage: 'Idioma de la interfaz',
    uiLanguageNote:
      'El idioma de la interfaz es independiente de los idiomas permitidos en tus partidas.',
    sound: 'Sonidos',
    haptics: 'Vibración (si el dispositivo lo permite)',
    motion: 'Animaciones',
    motionSystem: 'Según el sistema',
    motionReduced: 'Reducidas',
    motionFull: 'Normales',
    relaxHints: 'Pistas de validez en modo Relax',
  },
  rules: {
    title: 'Reglas del juego',
    goal: {
      title: 'Objetivo',
      body: [
        'Forma palabras con las 9 letras del sorteo tocándolas en el orden que quieras, sin necesidad de que estén juntas.',
        'Cada ficha se usa una sola vez por palabra; todas vuelven a estar disponibles para la siguiente. Mezclar no cambia el sorteo.',
        'Mínimo tres letras. Una misma palabra solo puntúa una vez por ronda.',
      ],
    },
    tiles: {
      title: 'Letras y tildes',
      body: [
        'Las tildes no tienen ficha propia: «corazón» se juega C-O-R-A-Z-O-N y se muestra con su tilde.',
        'La Ñ (español) y Ä, Ö, Ü (alemán) son fichas distintas, utilizables solo en su idioma: «año» y «ano» son palabras diferentes. ß se escribe con dos S; œ y æ usan dos fichas.',
      ],
    },
    languages: {
      title: 'Idiomas y asignación',
      body: [
        'Una palabra válida en varios de tus idiomas se asigna a uno solo. Elige el idioma antes de validar (botones Idioma) o deja «Auto».',
        'Auto elige el idioma válido que más palabras necesita para tu contrato y después el orden de tus idiomas. Una vez aceptada, la palabra no cambia de idioma.',
        'Se excluyen los nombres propios y las abreviaturas. Algunas palabras ofensivas se excluyen por la política de contenido. Podrás señalar una palabra rechazada.',
      ],
    },
    points: {
      title: 'Puntos',
      body: [
        'Una palabra de n letras (fichas) vale 10 × (n − 2): 3 letras = 10, 4 = 20 … 9 = 70.',
        'Puntuación final = puntos base × (1 + bonificación de idioma + bonificación de velocidad). Las bonificaciones se suman y el total se redondea una sola vez a la centésima.',
        'Una palabra rechazada no puntúa y no cuenta para el objetivo.',
      ],
    },
    contracts: {
      title: 'Contratos (Duelo táctico, Sprint libre)',
      body: [
        'Especialista (1 idioma): +10 %. Dúo (2 idiomas): +20 % con al menos 3 palabras en cada uno. Trío (3 idiomas): +30 % con al menos 3 palabras en cada uno.',
        'Contrato no cumplido: conservas tus puntos base, sin bonificación de idioma ni paso automático a un contrato inferior.',
        'Políglota común: +20 % (2 idiomas) o +30 % (3 idiomas) con al menos 3 palabras por idioma, igual para todos.',
      ],
    },
    speed: {
      title: 'Bonificación de velocidad (Duelo táctico)',
      body: [
        'Si llegas a 10 palabras válidas antes de 120 s: bonificación = 20 % × (120 − t) / 120, siendo t el tiempo oficial medido por el servidor. A los 90 s: +5 %; a los 60 s: +10 %; a los 30 s: +15 %.',
        'La 10.ª palabra aceptada termina tu ronda de inmediato. El juego te avisa si tu contrato no está cumplido.',
      ],
    },
    modes: {
      title: 'Modos',
      body: [
        'Clásico, Políglota común, Duelo táctico, Sprint libre, Relax (sin cronómetro ni clasificación) y entrenamiento contra robots.',
        'Empate exacto: el encuentro se declara empate, sin desempate oculto por velocidad.',
      ],
    },
    fairness: {
      title: 'Equidad',
      body: [
        'Un mismo sorteo no tiene la misma dificultad en todos los idiomas. Cada sorteo se comprueba para ofrecer suficientes palabras comunes a cada configuración, sin prometer una equidad perfecta.',
        'Los parámetros son hipótesis en prueba; una partida conserva siempre la versión de reglas y diccionarios con la que empezó.',
      ],
    },
    robots: {
      title: 'Robots',
      body: [
        'Los robots son programas deterministas (sin IA generativa) identificados como tales. Solo juegan palabras válidas y nunca entran en las clasificaciones humanas.',
      ],
    },
    keyboard: {
      title: 'Teclado',
      body: 'Escribe las letras (o 1–9 para la posición de una ficha), Intro para validar, Retroceso para borrar, Esc para vaciar, Espacio para mezclar.',
    },
  },
  about: {
    title: 'Acerca de',
    disclaimer:
      'Un juego de letras para disfrutar jugando en varios idiomas. No tiene una eficacia pedagógica demostrada.',
    dictionaries: 'Diccionarios y datos',
    frequencies: 'Frecuencias de palabras',
    policy: 'Política de contenido',
    adapted: 'lista adaptada',
    noticeFiles:
      'Los avisos completos (fuentes, versiones, licencias) acompañan a cada diccionario descargado.',
    noDefinitions:
      'No se muestran definiciones ni traducciones mientras no se integre una fuente con licencia compatible.',
  },
};

export default es;
